import { useEffect, useState, type JSX } from 'react';
import { Link, Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import {
  BadgeCheck,
  Code2,
  FileText,
  Lock,
  LogOut,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { Logo, toast } from '@caratuva/ui';
import {
  clearToken,
  getMode,
  getRole,
  getToken,
  isAuthenticated,
  isTokenExpired,
  setToken,
  type OrgMode,
} from '../lib/auth';
import { getProductionAccessStatus, switchMode } from '../lib/api';
import { NotificationsBell } from '../components/NotificationsBell';

/*
 * Authenticated shell.
 *
 * `beforeLoad` only enforces authentication — it runs before any children's
 * loaders fire, so unauthenticated users never trigger an API fetch on their
 * way to /login.
 *
 * There is deliberately NO blanket KYB/onboarding gate here. Caratuva is the
 * BlindPay account holder; integrators onboard their end clients as connected
 * accounts (ADR 0011) and use the developer surface (API keys, webhooks) +
 * notifications without their own KYB — matching the API, which gates only
 * money movement. The one KYB-gated surface is the invoice CMS (collecting to
 * the org's OWN account), and it self-gates per route: /invoices/new redirects
 * to /payout, and the invoice list shows an onboarding banner with a disabled
 * "New invoice" button until KYB + PIX are configured.
 *
 * The sidebar + header are intentionally minimal HTML for this scaffold;
 * Agent Z's component primitives (NavBar, Sidebar, Avatar) replace this
 * layout in a follow-up PR.
 */

export const Route = createFileRoute('/_app')({
  beforeLoad: ({ location }) => {
    if (getToken() && isTokenExpired()) {
      clearToken();
      throw redirect({
        to: '/login',
        search: { reason: 'expired', redirect: location.href },
      });
    }
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: AppShell,
});

// Test/live indicator + segmented toggle. Renders both environments as a
// two-segment control with the active mode highlighted (rather than a single
// flip button) so the current environment is always legible at a glance.
// Switching re-mints the JWT for the chosen mode (lib/api.switchMode) and
// hard-reloads at the app root so per-mode data (e.g. invoice-creation
// eligibility, which is KYB/PIX-gated per mode) re-fetches against the new mode.
//
// The live segment is gated: switching test→live requires production approval
// (#77). When unapproved, the segment shows a lock and a click routes to the
// Onboarding page's production-access section (/onboarding#production-access)
// instead of attempting a switch the server would reject (switch-mode 400s
// ProductionAccessNotApproved). Switching live→test is always allowed.
function ModeSwitcher(): JSX.Element {
  const intl = useIntl();
  const navigate = useNavigate();
  const mode = getMode();
  const [busy, setBusy] = useState<OrgMode | null>(null);

  const access = useQuery({
    queryKey: ['production-access-status'],
    queryFn: ({ signal }) => getProductionAccessStatus(signal),
  });
  const liveApproved = access.data?.liveAccessApproved ?? false;

  async function switchTo(target: OrgMode): Promise<void> {
    if (busy !== null || target === mode) return;
    if (target === 'live' && !liveApproved) {
      void navigate({ to: '/onboarding', hash: 'production-access' });
      return;
    }
    setBusy(target);
    try {
      const res = await switchMode(target);
      setToken(res.token);
      // Hard reload at the root so all per-mode queries re-fetch for the
      // newly active mode.
      window.location.assign('/');
    } catch (err) {
      setBusy(null);
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div
      role="group"
      aria-label={intl.formatMessage({
        id: 'mode.group.label',
        defaultMessage: 'Environment mode',
      })}
      className="inline-flex items-center gap-0.5 rounded-lg border border-navy-200 bg-navy-50 p-0.5"
    >
      <ModeSegment
        active={mode === 'test'}
        disabled={busy !== null}
        dotClassName="bg-amber-500"
        label={intl.formatMessage({ id: 'mode.tab.test', defaultMessage: 'Test' })}
        title={intl.formatMessage(
          { id: 'mode.switchTo', defaultMessage: 'Switch to {mode} mode' },
          { mode: 'test' },
        )}
        onClick={() => void switchTo('test')}
      />
      <ModeSegment
        active={mode === 'live'}
        disabled={busy !== null}
        dotClassName="bg-emerald-500"
        locked={!liveApproved}
        label={intl.formatMessage({ id: 'mode.tab.live', defaultMessage: 'Live' })}
        title={
          liveApproved
            ? intl.formatMessage(
                { id: 'mode.switchTo', defaultMessage: 'Switch to {mode} mode' },
                { mode: 'live' },
              )
            : intl.formatMessage({
                id: 'mode.live.needsApproval',
                defaultMessage: 'Request production access in Onboarding to unlock live mode',
              })
        }
        onClick={() => void switchTo('live')}
      />
    </div>
  );
}

function ModeSegment({
  active,
  disabled,
  dotClassName,
  label,
  title,
  locked = false,
  onClick,
}: {
  active: boolean;
  disabled: boolean;
  dotClassName: string;
  label: string;
  title: string;
  locked?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={title}
      className={
        'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60 ' +
        (active ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-500 hover:text-navy-800')
      }
    >
      <span
        aria-hidden
        className={`inline-block h-2 w-2 rounded-full ${active ? dotClassName : 'bg-navy-300'}`}
      />
      {label}
      {locked ? <Lock aria-hidden className="h-3 w-3" strokeWidth={1.5} /> : null}
    </button>
  );
}

function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const intl = useIntl();

  function onSignOut(): void {
    clearToken();
    void navigate({ to: '/login' });
  }

  // Kick an idle tab out once its JWT expires, even if it never issues an API
  // call that would otherwise trigger the 401 interceptor in lib/api.ts.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (isTokenExpired()) {
        clearToken();
        window.location.assign('/login?reason=expired');
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="grid min-h-screen grid-cols-[16rem_1fr]">
      <aside className="border-r border-navy-200 bg-white">
        <div className="px-6 py-5">
          <Logo size={24} />
        </div>
        <nav className="px-3">
          <Link
            to="/invoices"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900"
            activeProps={{ className: 'active' }}
          >
            <FileText aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'nav.invoices', defaultMessage: 'Invoices' })}
          </Link>
          <Link
            to="/customers"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900"
            activeProps={{ className: 'active' }}
            activeOptions={{ exact: false, includeSearch: false }}
          >
            <Users aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'nav.customers', defaultMessage: 'Customers' })}
          </Link>
          <Link
            to="/onboarding"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900"
            activeProps={{ className: 'active' }}
          >
            <BadgeCheck aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'nav.onboarding', defaultMessage: 'Onboarding' })}
          </Link>
          <Link
            to="/bank-account"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900"
            activeProps={{ className: 'active' }}
          >
            <Wallet aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'nav.bankAccount', defaultMessage: 'Bank account' })}
          </Link>
          {/* Notifications hidden until the feature is fully implemented */}
          {/* <Link
            to="/notifications"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900"
            activeProps={{ className: 'active' }}
          >
            <Bell aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'nav.notifications', defaultMessage: 'Notifications' })}
          </Link> */}
          <Link
            to="/developers"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900"
            activeProps={{ className: 'active' }}
            activeOptions={{ exact: false, includeSearch: false }}
          >
            <Code2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'nav.developers', defaultMessage: 'Developers' })}
          </Link>
          {getRole() === 'ops' ? (
            <Link
              to="/admin/production-access"
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy-800 hover:bg-navy-50 [&.active]:bg-navy-100 [&.active]:text-navy-900"
              activeProps={{ className: 'active' }}
            >
              <ShieldCheck aria-hidden className="h-4 w-4" strokeWidth={1.5} />
              {intl.formatMessage({ id: 'nav.admin', defaultMessage: 'Admin' })}
            </Link>
          ) : null}
        </nav>
      </aside>

      <div className="flex min-h-screen flex-col">
        <header className="flex items-center justify-end gap-2 border-b border-navy-200 bg-white px-6 py-3">
          <ModeSwitcher />
          <NotificationsBell />
          <button
            type="button"
            onClick={onSignOut}
            className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-navy-700 hover:bg-navy-50"
          >
            <LogOut aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'nav.signOut', defaultMessage: 'Sign out' })}
          </button>
        </header>
        <main className="flex-1 px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
